import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";


import {
  setAnchorElId,
  closePopover,
  setMenuName,
  setFoodItemToAdd,
  openMenuNameDialog,
  closeMenuNameDialog,
  fetchMenus,
  setCurrentMenu,
  setSelectedMenuId,
  setCurrentMenuIndex,
  openConfirmDeleteDialog,
  closeConfirmDeleteDialog,
  openItemDeleteDialog,
  closeItemDeleteDialog,
  deleteMenuFromServer,
  deleteMenuItemFromServer,
  // updateMenu
} from "src/redux/slices/menuSlice";

import {
  parsePrice,
  getCurrentDateTimeJson,
  getFormattedCreationTime,
} from "./menuUtils";
import {
  createMenuOnServer,
  updateItemQuantity,
  updateMenuOnServer,
} from "./menuService";

// Shared across card/header hook instances so one click cannot submit twice.
const pendingMutations = new Set();

const useMenuActions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { selectedMenus, menuName, foodItemToAdd } = useSelector(
    (state) => state.menus
  );

  /** ------------------ MENU NAME DIALOG ------------------ **/
  const handleOpenMenuNameDialog = (event, fooditem) => {
    if (!isAuthenticated) {
      toast.error("Please login first to create a menu.");
      dispatch(closeMenuNameDialog());
      navigate("/login");
      return;
    }

    if (!event?.currentTarget || fooditem === undefined) {
      console.error("Event or food item is missing.");
      return;
    }

    const anchorId = event.currentTarget.id;
    const popoverID = fooditem ? fooditem.id : "header";

    dispatch(setAnchorElId(anchorId));
    dispatch(closePopover());
    dispatch(setMenuName(""));
    dispatch(setFoodItemToAdd(fooditem || null));

    dispatch(
      openMenuNameDialog({
        anchorElId: anchorId,
        popoverID,
        foodItem: fooditem
          ? {
              ...fooditem,
              quantity: fooditem.quantity || 1,
              price: parsePrice(fooditem.item_price),
            }
          : null,
        creationTime: getFormattedCreationTime(),
      })
    );
  };

  const handleCloseMenuNameDialog = () => dispatch(closeMenuNameDialog());

  const handleInputChange = (e) => dispatch(setMenuName(e.target.value));

  /** ------------------ SAVE MENU ------------------ **/
  const handleSaveMenuName = async () => {
    const name = menuName?.trim();
    const customerId = localStorage.getItem("customerId");
    if (!name) return toast.error("Menu name cannot be empty.");
    if (!customerId) return toast.error("Please log in to save your menu.");
    if (selectedMenus.some((menu) => menu.menuName === name)) {
      return toast.warn("Menu name already exists.");
    }
    const key = `create:${customerId}:${name}`;
    if (pendingMutations.has(key)) return;
    pendingMutations.add(key);
    try {
      const sid = foodItemToAdd?.sid ?? foodItemToAdd?.id;
      const response = await createMenuOnServer({
        menu_name: name,
        sid: sid == null ? [] : [sid],
        menu_schedule: getCurrentDateTimeJson(),
        menu_cid: customerId,
        table_number: 2,
      });
      const menuId = response.data.data.menu_id;
      localStorage.setItem("menu_id", menuId);
      dispatch(setSelectedMenuId(menuId));
      dispatch(closeMenuNameDialog());
      dispatch(closePopover());
      await dispatch(fetchMenus());
      toast.success("Menu created and saved.");
    } catch (error) {
      toast.error("Couldn’t save your menu. Please try again.");
    } finally {
      pendingMutations.delete(key);
    }
  };

  const changeQuantity = async (menuName, itemId, operation) => {
    const menu = selectedMenus.find((entry) => entry.menuName === menuName);
    const item = menu?.items.find((entry) => String(entry.sid ?? entry.id) === String(itemId));
    if (!item || (operation === "decrement" && item.quantity <= 1)) return;
    const key = `item:${menu.menu_id}:${itemId}`;
    if (pendingMutations.has(key)) return;
    pendingMutations.add(key);
    try {
      await updateItemQuantity(localStorage.getItem("customerId"), menu.menu_id, itemId, operation);
      await dispatch(fetchMenus());
    } catch (error) {
      toast.error("Couldn’t update the quantity. Please try again.");
    } finally {
      pendingMutations.delete(key);
    }
  };
  const handleIncrement = (name, id) => changeQuantity(name, id, "increment");
  const handleDecrement = (name, id) => changeQuantity(name, id, "decrement");

  /** ------------------ MENU SELECTION ------------------ **/
  const handleMenuSelection = (menuName, menu_id) => {
    const selectedMenu = selectedMenus.find(
      (menu) =>
        menu.menuName === menuName &&
        String(menu.menuId ?? menu.menu_id) === String(menu_id)
    );
    if (!selectedMenu) return console.error("Selected menu not found.");

    localStorage.setItem("menu_id", menu_id);
    dispatch(setCurrentMenu(selectedMenu));
    dispatch(setSelectedMenuId(menu_id));
    dispatch(
      setCurrentMenuIndex(
        selectedMenus.findIndex((menu) => menu.menuName === menuName)
      )
    );
  };

  /** ------------------ MENU DELETE ------------------ **/
  const handleOpenMenuDeleteDialog = (menuName, menu_id) => {
    dispatch(closePopover());
    dispatch(setSelectedMenuId(menu_id));
    dispatch(openConfirmDeleteDialog({ menuName, menu_id }));
  };

  const handleDeleteMenu = async (menu_id) => {
    dispatch(setSelectedMenuId(menu_id));
    try {
      await dispatch(deleteMenuFromServer(menu_id)).unwrap();
      dispatch(closeConfirmDeleteDialog());
      await dispatch(fetchMenus());
    } catch (err) {
      toast.error("Couldn’t delete your menu. Please try again.");
    }
  };

  const handleCloseMenuDeleteDialog = () =>
    dispatch(closeConfirmDeleteDialog());

  /** ------------------ ITEM DELETE ------------------ **/
  const handleOpenItemDeleteDialog = (event, menuName, fooditem) => {
    if (!event?.currentTarget || !menuName || !fooditem) return;
    dispatch(setAnchorElId(event.currentTarget.id));
    dispatch(
      openItemDeleteDialog({
        anchorElId: event.currentTarget.id,
        deletePopoverId: fooditem.sid ?? fooditem.id,
        menuName,
        fooditem,
      })
    );
  };

  const handleDeleteItem = async (menuName, itemId) => {
    const menu = selectedMenus.find((entry) => entry.menuName === menuName);
    if (!menu || itemId == null) return;
    const key = `item:${menu.menu_id}:${itemId}`;
    if (pendingMutations.has(key)) return;
    pendingMutations.add(key);
    try {
      await dispatch(deleteMenuItemFromServer({ menu_id: menu.menu_id, menuName, sid: itemId })).unwrap();
      await dispatch(fetchMenus());
      toast.success("Item removed from menu.");
    } catch (error) {
      toast.error("Couldn’t remove this item. Please try again.");
    } finally {
      pendingMutations.delete(key);
    }
  };

  const handleCloseDeleteItemDialog = () => dispatch(closeItemDeleteDialog());

  const handleAddItemToExistingMenu = async (menuName, fooditem) => {
    const menu = selectedMenus.find((entry) => entry.menuName === menuName);
    const sid = fooditem?.sid ?? fooditem?.id;
    if (!menu?.menu_id || sid == null) return;
    const key = `item:${menu.menu_id}:${sid}`;
    if (pendingMutations.has(key)) return;
    pendingMutations.add(key);
    dispatch(closePopover());
    try {
      await updateMenuOnServer(localStorage.getItem("customerId"), menu.menu_id, {
        items: [{ sid, quantity: 1 }],
      });
      dispatch(setSelectedMenuId(menu.menu_id));
      await dispatch(fetchMenus());
      toast.success(`${fooditem.item_name} added to ${menuName}`);
    } catch (error) {
      toast.error("Couldn’t add this item. Please try again.");
    } finally {
      pendingMutations.delete(key);
    }
  };

  return {
    handleOpenMenuNameDialog,
    handleCloseMenuNameDialog,
    handleSaveMenuName,
    handleInputChange,
    handleMenuSelection,
    handleOpenMenuDeleteDialog,
    handleCloseMenuDeleteDialog,
    handleOpenItemDeleteDialog,
    handleCloseDeleteItemDialog,
    handleDeleteMenu,
    handleDeleteItem,
    handleIncrement,
    handleDecrement,
    handleAddItemToExistingMenu,
  };
};

export default useMenuActions;
